import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import {
  Check,
  X,
  User,
  DollarSign,
  Calendar,
  Building2,
  MessageSquare,
} from 'lucide-react';

const approvalSchema = z.object({
  comments: z.string().optional(),
});

type ApprovalFormData = z.infer<typeof approvalSchema>;

interface ExpenseApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense: any;
  onSubmit: (action: 'approved' | 'rejected', comments?: string) => void;
}

export default function ExpenseApprovalDialog({
  open,
  onOpenChange,
  expense,
  onSubmit,
}: ExpenseApprovalDialogProps) {
  const [action, setAction] = useState<'approved' | 'rejected' | null>(null);
  const [loading, setLoading] = useState(false);

  const form = useForm<ApprovalFormData>({
    resolver: zodResolver(approvalSchema),
    defaultValues: {
      comments: '',
    },
  });

  if (!expense) return null;

  const handleAction = async (selectedAction: 'approved' | 'rejected') => {
    if (selectedAction === 'approved') {
      // For approval, comments are optional, so we can submit immediately
      setAction(selectedAction);
      setLoading(true);
      try {
        await onSubmit('approved', '');
        // Reset form and close dialog on success
        form.reset();
        setAction(null);
        onOpenChange(false);
      } catch (error) {
        console.error('Approval failed:', error);
      } finally {
        setLoading(false);
      }
    } else {
      // For rejection, show the form to require comments
      setAction(selectedAction);
    }
  };

  const handleSubmit = async (data: ApprovalFormData) => {
    if (!action) return;

    // Require comments for rejection
    if (action === 'rejected' && !data.comments?.trim()) {
      form.setError('comments', { message: 'Comments are required for rejection' });
      return;
    }

    setLoading(true);
    try {
      await onSubmit(action, data.comments?.trim() || undefined);
      // Reset form and close dialog on success
      form.reset();
      setAction(null);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Approval failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = () => {
    const colors = {
      company: 'bg-blue-100 text-blue-800',
      personal: 'bg-purple-100 text-purple-800',
      reimbursement: 'bg-orange-100 text-orange-800'
    };
    return (
      <Badge variant="outline" className={colors[expense.expense_type as keyof typeof colors]}>
        {expense.expense_type.charAt(0).toUpperCase() + expense.expense_type.slice(1)}
      </Badge>
    );
  };

  const resetDialog = () => {
    setAction(null);
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetDialog}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2" />
            Expense Approval
          </DialogTitle>
          <DialogDescription>
            Review and approve or reject this expense request
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Expense Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expense Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{expense.title}</h3>
                  {expense.description && (
                    <p className="text-sm text-muted-foreground mt-1">{expense.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">${expense.amount}</div>
                  {getTypeBadge()}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t">
                <div className="flex items-center text-sm">
                  <User className="h-4 w-4 mr-2 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{expense.user_name}</p>
                    <p className="text-muted-foreground">Submitted by</p>
                  </div>
                </div>

                {expense.branch_name && (
                  <div className="flex items-center text-sm">
                    <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{expense.branch_name}</p>
                      <p className="text-muted-foreground">Branch</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{format(new Date(expense.expense_date), 'MMM dd, yyyy')}</p>
                    <p className="text-muted-foreground">Expense date</p>
                  </div>
                </div>
              </div>

              {/* Category and Payment Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <div className="flex items-center mt-1">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: expense.category_color }}
                    />
                    <span className="font-medium">{expense.category_name}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
                  <p className="font-medium mt-1">
                    {expense.payment_method.split('_').map((word: string) => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </p>
                </div>
              </div>

              {(expense.vendor_name || expense.receipt_number) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
                  {expense.vendor_name && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Vendor</label>
                      <p className="font-medium mt-1">{expense.vendor_name}</p>
                    </div>
                  )}

                  {expense.receipt_number && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Receipt #</label>
                      <p className="font-medium mt-1">{expense.receipt_number}</p>
                    </div>
                  )}
                </div>
              )}

              {expense.notes && (
                <div className="pt-3 border-t">
                  <label className="text-sm font-medium text-muted-foreground">Notes</label>
                  <p className="text-sm mt-1 bg-gray-50 p-2 rounded">{expense.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Selection or Comment Form */}
          {!action ? (
            <div className="flex space-x-4">
              <Button
                onClick={() => handleAction('approved')}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                onClick={() => setAction('rejected')}
                variant="destructive"
                className="flex-1"
                disabled={loading}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          ) : action === 'rejected' ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rejection Reason *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Please provide a reason for rejecting this expense..."
                          {...field}
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAction(null)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    className="flex-1"
                    disabled={loading}
                  >
                    {loading ? 'Rejecting...' : 'Confirm Rejection'}
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <div className="text-center py-4">
              <div className="text-green-600 mb-2">
                <Check className="h-8 w-8 mx-auto" />
              </div>
              <p className="font-medium">Approving expense...</p>
            </div>
          )}
        </div>

        {!action && (
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}